import { useState, useEffect, useRef } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const STAT_TILES = [
  { stat: "46%", description: "of users report negative experiences on dating apps", source: "Source: Pew Research Center (2023)", color: "#D1D5DB" },
  { stat: "48%", description: "have experienced unwanted behavior on a dating app", source: "Source: Pew Research Center (2023)", color: "#D1D5DB" },
  { stat: "22%", description: "say dating apps make finding a long-term partner harder", source: "Source: Pew Research Center (2023)", color: "#D1D5DB" },
  { stat: "1,000+", description: "studies show too many options reduce satisfaction", source: "Source: Columbia Business School", color: "#D1D5DB" },
];

export default function ProblemPage() {
  const trackRef = useRef(null);
  const [pos, setPos] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartScroll, setDragStartScroll] = useState(0);
  const count = STAT_TILES.length;

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isDragging) setPos((p) => p + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [isDragging]);

  useEffect(() => {
    if (pos >= count) {
      const timeout = setTimeout(() => {
        if (trackRef.current) {
          trackRef.current.style.transition = "none";
          setPos(0);
          requestAnimationFrame(() => {
            if (trackRef.current) trackRef.current.style.transition = "";
          });
        }
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [pos, count]);

  const handlePointerDown = (e) => {
    setIsDragging(true);
    setDragStartX(e.touches ? e.touches[0].pageX : e.pageX);
    setDragStartScroll(pos);
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;
    const pageX = e.changedTouches ? e.changedTouches[0].pageX : e.pageX;
    const diff = dragStartX - pageX;
    if (Math.abs(diff) > 50) setPos(dragStartScroll + (diff > 0 ? 1 : -1));
    setIsDragging(false);
  };

  const extended = [...STAT_TILES, ...STAT_TILES.slice(0, 3)];
  const tileW = 340;
  const gap = 32;

  return (
    <div className="min-h-screen bg-white" data-testid="problem-page">
      <SEO
        title="The Problem with Dating"
        path="/problem"
        description="46% of dating app users report negative experiences. The problem isn't people — it's the system. See the data behind why HAEVN exists."
      />
      <Navigation />

      <section className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-4">The Problem</p>
            <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl text-haevn-navy leading-tight mb-6">
              Dating isn't broken. The system is.
            </h1>
            <p className="text-base md:text-lg text-gray-500 font-body leading-relaxed max-w-2xl">
              The issue isn't people. It's the system they're using.
            </p>
          </FadeIn>
        </div>
      </section>

      <div className="relative overflow-hidden py-4">
        <div
          ref={trackRef}
          className="flex select-none cursor-grab active:cursor-grabbing px-6 md:px-12 transition-transform duration-500 ease-out"
          style={{ transform: `translateX(calc(-${pos * (tileW + gap)}px))`, gap: `${gap}px` }}
          onMouseDown={handlePointerDown}
          onMouseUp={handlePointerUp}
          onMouseLeave={() => setIsDragging(false)}
          onTouchStart={handlePointerDown}
          onTouchEnd={handlePointerUp}
          data-testid="problem-stats-track"
        >
          {extended.map((tile, i) => (
            <div
              key={i}
              className="shrink-0 flex flex-col justify-between bg-white border border-gray-200 rounded-2xl p-8 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.1)] transition-shadow duration-300"
              style={{ width: `${tileW}px`, minHeight: "220px" }}
              data-testid={i < count ? `problem-tile-${i}` : undefined}
            >
              <div>
                <p
                  className="font-body mb-5"
                  style={{ fontSize: "clamp(52px, 6vw, 72px)", fontWeight: 800, color: tile.color, lineHeight: 1, letterSpacing: "-0.02em" }}
                >
                  {tile.stat}
                </p>
                <p className="text-base text-haevn-navy font-body leading-relaxed">
                  {tile.description}
                </p>
              </div>
              <p className="text-xs text-[#6B7280] font-body mt-6">
                {tile.source}
              </p>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-20 md:w-32 bg-gradient-to-l from-white to-transparent" />
      </div>

      <section className="py-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          <FadeIn>
            <p className="text-base text-gray-500 font-body leading-relaxed italic mb-12">
              Most people don't need better profiles. They need a better system.
            </p>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="space-y-6">
              <p className="text-base text-gray-600 font-body leading-relaxed">
                For the last decade, online dating has mostly meant one thing: swiping. Swipe right. Swipe left. Repeat. The platforms reward attention, not alignment. The loudest profiles rise to the top, the best photos get the most visibility, and people end up chasing matches instead of finding real compatibility.
              </p>
              <p className="text-base text-gray-600 font-body leading-relaxed">
                The data confirms what most people already feel. Nearly half of all dating app users report negative experiences. Unwanted messages are routine. And a growing number of people say these platforms have actually made it harder to find a long-term partner.
              </p>
              <p className="text-base text-gray-600 font-body leading-relaxed">
                The problem isn't the people using these apps. It's the model itself — one built around endless browsing, superficial signals, and engagement metrics that prioritize time spent over connections made.
              </p>
              <p className="text-base text-gray-500 font-body leading-relaxed italic">
                HAEVN was built to do the opposite.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
